import React, { useState } from "react";
import { StatusPill, Tooltip, ConfirmDialog, Toast, ProgressBar, Button } from "components";
import { Stage, ConfirmDemo } from "./_shared";

const STATUSES = ["draft", "awaiting-review", "ready", "in-progress", "played", "archived"];

const ToastDemo = ({ severity = "success", message, label, autoHideDuration }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button style={{ font: "inherit", fontSize: 13, fontWeight: 600, padding: "8px 14px", borderRadius: 8, border: "1px solid var(--surface-border,#e2e8f0)", background: "var(--accent,#2ec4b6)", color: "var(--accent-on,#fff)", cursor: "pointer" }} onClick={() => setOpen(true)}>{label}</button>
      <Toast open={open} message={message} severity={severity} autoHideDuration={autoHideDuration} onClose={() => setOpen(false)} />
    </>
  );
};

export const feedbackEntries = [
  {
    id: "status-pill", name: "StatusPill", group: "Status & feedback", status: "live",
    description: "Lifecycle status pill. Colors are fixed per status (a draft is always orange) for consistent recognition.",
    props: [
      { name: "status", type: "'draft'|'ready'|'in-progress'|'played'|'archived'|'awaiting-review'", description: "Lifecycle" },
      { name: "size", type: "'sm'|'md'", default: "'md'", description: "Size" },
    ],
    variants: [
      { label: "All statuses (manager)", render: () => <Stage width={420}><div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{STATUSES.map((s) => <StatusPill key={s} status={s} />)}</div></Stage> },
      { label: "On game surface", render: () => <Stage surface="game" width={420}><div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{STATUSES.map((s) => <StatusPill key={s} status={s} />)}</div></Stage> },
    ],
    code: `import { StatusPill } from "components";

<StatusPill status="ready" />
<StatusPill status="awaiting-review" />
<StatusPill status="in-progress" size="sm" />`,
  },
  {
    id: "tooltip", name: "Tooltip", group: "Status & feedback", status: "live",
    description: "Brand navy/white tooltip (wraps MUI). Used by every §10 placeholder.",
    props: [{ name: "title", type: "node", description: "Content" }, { name: "placement", type: "string", default: "'top'", description: "Position" }],
    variants: [{ label: "Hover the button", render: () => <Stage><Tooltip title="Search across your games — coming soon"><span><Button variant="secondary">Hover me</Button></span></Tooltip></Stage> }],
    code: `import { Tooltip } from "components";

<Tooltip title="Search across your games — coming soon">
  <span><Button variant="secondary">Hover me</Button></span>
</Tooltip>`,
  },
  {
    id: "confirm-dialog", name: "ConfirmDialog", group: "Status & feedback", status: "live",
    description: "Confirmation dialog — brand Paper + Button actions. Simple mode (cancel + confirm, destructive toggles red) or an `actions` array for a positive CTA or a 3-button layout.",
    props: [
      { name: "open / onConfirm / onCancel", type: "—", description: "Simple-mode control" },
      { name: "destructive", type: "bool", default: "false", description: "Red confirm (simple mode)" },
      { name: "actions", type: "[{label,onClick,variant?,…}]", description: "Custom buttons (positive / 3-button)" },
      { name: "loading", type: "bool", default: "false", description: "Spinner on confirm" },
    ],
    variants: [
      { label: "Destructive", render: () => <ConfirmDemo trigger="Reset game…" render={(open, close) => <ConfirmDialog open={open} title="Reset this game?" message="This clears players, scores, and runtime state. Questions stay unchanged." confirmLabel="Reset Game" destructive onCancel={close} onConfirm={close} />} /> },
      { label: "Positive CTA", render: () => <ConfirmDemo trigger="Mark ready…" render={(open, close) => <ConfirmDialog open={open} title="Mark this game as ready?" message="It moves to your Ready list — launch it any time." actions={[{ label: "Not yet", variant: "secondary", onClick: close }, { label: "Mark Ready", variant: "cta", onClick: close, autoFocus: true }]} />} /> },
      { label: "Three actions", render: () => <ConfirmDemo trigger="Leave game…" render={(open, close) => <ConfirmDialog open={open} title="Game in progress" message="You have an active session. Discard it, or keep playing?" actions={[{ label: "Cancel", variant: "secondary", onClick: close }, { label: "Discard session", variant: "danger", onClick: close }, { label: "Resume", variant: "cta", onClick: close, autoFocus: true }]} />} /> },
    ],
    notes: ["Wired into ResetGameButton + DeleteGameButton (simple mode)."],
    code: `import { ConfirmDialog } from "components";

// Simple — cancel + destructive confirm:
<ConfirmDialog
  open={open}
  title="Reset this game?"
  message="This clears players, scores, and runtime state."
  confirmLabel="Reset Game"
  destructive
  onCancel={close}
  onConfirm={reset}
/>

// Three actions — default + destructive + positive:
<ConfirmDialog
  open={open}
  title="Game in progress"
  message="Discard the session, or keep playing?"
  actions={[
    { label: "Cancel", variant: "secondary", onClick: close },
    { label: "Discard session", variant: "danger", onClick: discard },
    { label: "Resume", variant: "cta", onClick: resume, autoFocus: true },
  ]}
/>`,
  },
  {
    id: "toast", name: "Toast", group: "Status & feedback", status: "live",
    description: "Transient feedback — brand fonts + token-mapped fills (teal success, red error, gold warning, navy info). Auto-closes after autoHideDuration; pass null to keep it open until dismissed.",
    props: [
      { name: "severity", type: "'success'|'info'|'warning'|'error'", default: "'success'", description: "Tone / fill color" },
      { name: "autoHideDuration", type: "number | null", default: "6000", description: "ms before auto-close; null = stay until dismissed" },
      { name: "open / onClose / message", type: "—", description: "Control + content" },
      { name: "anchorOrigin", type: "MUI anchor", description: "Position (optional)" },
    ],
    variants: [
      { label: "Severities", render: () => <Stage><div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><ToastDemo severity="success" label="Success" message="Game has been reset!" /><ToastDemo severity="info" label="Info" message="Generating your game…" /><ToastDemo severity="warning" label="Warning" message="Some clues are still empty." /><ToastDemo severity="error" label="Error" message="Couldn’t save — try again." /></div></Stage> },
      { label: "Auto-close vs persistent", render: () => <Stage><div style={{ display: "flex", gap: 8 }}><ToastDemo severity="success" label="Auto-close (3s)" message="Saved!" autoHideDuration={3000} /><ToastDemo severity="info" label="Stays until dismissed" message="Click × to close." autoHideDuration={null} /></div></Stage> },
    ],
    code: `import { Toast } from "components";

<Toast open={open} severity="success" message="Game has been reset!" onClose={close} />
<Toast open={open} severity="error" message="Couldn't save — try again." onClose={close} />

// Stay open until dismissed:
<Toast open={open} severity="info" message="Click × to close." autoHideDuration={null} onClose={close} />`,
  },
  {
    id: "progress-bar", name: "ProgressBar", group: "Status & feedback", status: "live",
    description: "Determinate completion bar, surface-aware. Solid tones, or a floating teal/gold lava-lamp fill (tone='lava').",
    props: [
      { name: "value", type: "number", default: "0", description: "0–100" },
      { name: "tone", type: "'accent'|'gold'|'warn'|'lava'", default: "'accent'", description: "Fill (lava = animated)" },
      { name: "height", type: "number", default: "8", description: "px (lava looks best taller, ~14)" },
    ],
    variants: [
      { label: "Solid tones", render: () => <Stage width={240}><div style={{ display: "grid", gap: 12 }}><ProgressBar value={35} label="35% complete" /><ProgressBar value={80} /><ProgressBar value={100} tone="gold" /></div></Stage> },
      { label: "Lava (animated)", render: () => <Stage width={280}><div style={{ display: "grid", gap: 12 }}><ProgressBar value={72} tone="lava" height={14} label="Generating your game…" /><ProgressBar value={100} tone="lava" height={14} /></div></Stage> },
      { label: "Game surface", render: () => <Stage surface="game" width={280}><ProgressBar value={60} tone="lava" height={14} label="60% complete" /></Stage> },
    ],
    code: `import { ProgressBar } from "components";

<ProgressBar value={60} label="60% complete" />
<ProgressBar value={100} tone="gold" />

// Floating teal/gold lava-lamp fill (taller reads best):
<ProgressBar value={72} tone="lava" height={14} label="Generating…" />`,
  },
];
